import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Check,
  ChevronDown,
  CircleStop,
  Cloud,
  Code2,
  Container,
  Copy,
  FolderGit2,
  KeyRound,
  Laptop,
  LoaderCircle,
  LogOut,
  Paperclip,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  TerminalSquare,
  Trash2,
  UploadCloud,
  Wifi,
  X,
} from 'lucide-react';
import { agentCatalog, type AgentId, type Environment, type Session } from '@devpilot/protocol';
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { auth, db, googleProvider, storage } from './firebase';
import { SessionActionsMenu } from './SessionActionsMenu';
import { Terminal } from './Terminal';

type View = 'sessions' | 'environments' | 'settings';
type Toast = { id: number; tone: 'success' | 'error' | 'info'; message: string };

type SettingsData = {
  defaultAgent?: AgentId;
  defaultModels?: Partial<Record<AgentId, string>>;
  defaultEfforts?: Partial<Record<AgentId, string>>;
  ollamaApiKeyConfigured?: boolean;
  ollamaApiKeyError?: string | null;
};

const ownerEmail = 'soissonyan@gmail.com';
const ownerUid = 'grvJwFOtkjPDIGMKBsgyLLpTaYl1';

async function bootstrapOwnership(user: User) {
  if (
    user.uid !== ownerUid ||
    !user.email ||
    user.email.toLowerCase() !== ownerEmail ||
    !user.emailVerified
  ) {
    throw new Error('Use the authorized, verified Google account.');
  }
  await setDoc(
    doc(db, 'system/access'),
    {
      ownerUid: user.uid,
      ownerEmail: user.email,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  await setDoc(
    doc(db, `users/${user.uid}`),
    {
      email: user.email,
      displayName: user.displayName,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  const settingsRef = doc(db, `users/${user.uid}/settings/main`);
  if (!(await getDoc(settingsRef)).exists()) {
    await setDoc(settingsRef, {
      defaultAgent: 'codex',
      defaultModels: {},
      defaultEfforts: {},
      ollamaApiKeyConfigured: false,
      ollamaApiKeyError: null,
      terminalTheme: 'obsidian',
      updatedAt: serverTimestamp(),
    });
  }
}

async function createSessionRecord(
  uid: string,
  input: {
    title: string;
    agent: AgentId;
    model: string;
    effort: string;
    environmentId: string | null;
  },
) {
  const session = doc(collection(db, `users/${uid}/sessions`));
  const command = doc(collection(db, `users/${uid}/commands`));
  const batch = writeBatch(db);
  batch.set(session, {
    ...input,
    ownerUid: uid,
    status: 'queued',
    archived: false,
    containerName: null,
    lastError: null,
    writerClientId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.set(command, {
    ownerUid: uid,
    sessionId: session.id,
    type: 'provision',
    status: 'queued',
    workerId: null,
    attempts: 0,
    error: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return session.id;
}

async function queueSessionAction(
  uid: string,
  sessionId: string,
  action: 'provision' | 'start' | 'stop' | 'restart' | 'archive' | 'delete',
) {
  if (action === 'archive') {
    await setDoc(
      doc(db, `users/${uid}/sessions/${sessionId}`),
      { archived: true, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }
  await addDoc(collection(db, `users/${uid}/commands`), {
    ownerUid: uid,
    sessionId,
    type: action,
    status: 'queued',
    workerId: null,
    attempts: 0,
    error: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

function fromDocument<T>(snapshot: QueryDocumentSnapshot<DocumentData>): T {
  return { id: snapshot.id, ...snapshot.data() } as T;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message.replace(/^Firebase:\s*/i, '');
  return String(error);
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [ownerReady, setOwnerReady] = useState(false);
  const [authError, setAuthError] = useState('');
  const [view, setView] = useState<View>('sessions');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [settings, setSettings] = useState<SettingsData>({ defaultAgent: 'codex' });
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showNewEnvironment, setShowNewEnvironment] = useState(false);
  const [workerOnline, setWorkerOnline] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const selectedSession = sessions.find((session) => session.id === selectedSessionId) ?? null;

  const toast = (message: string, tone: Toast['tone'] = 'info') => {
    const id = ++toastId.current;
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4200);
  };

  useEffect(
    () =>
      onAuthStateChanged(auth, (nextUser) => {
        setUser(nextUser);
        setAuthReady(true);
        setOwnerReady(false);
      }),
    [],
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    bootstrapOwnership(user)
      .then(() => {
        if (!cancelled) setOwnerReady(true);
      })
      .catch(async (error) => {
        if (!cancelled) {
          setAuthError(errorMessage(error));
          if (user.uid !== ownerUid) await signOut(auth);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !ownerReady) return;
    const sessionQuery = query(
      collection(db, `users/${user.uid}/sessions`),
      orderBy('updatedAt', 'desc'),
    );
    const environmentQuery = query(
      collection(db, `users/${user.uid}/environments`),
      orderBy('updatedAt', 'desc'),
    );
    const stopSessions = onSnapshot(
      sessionQuery,
      (snapshot) => {
        const next = snapshot.docs.map((item) => fromDocument<Session>(item));
        setSessions(next);
        setSelectedSessionId((current) => current ?? next[0]?.id ?? null);
      },
      (error) => toast(errorMessage(error), 'error'),
    );
    const stopEnvironments = onSnapshot(
      environmentQuery,
      (snapshot) => setEnvironments(snapshot.docs.map((item) => fromDocument<Environment>(item))),
      (error) => toast(errorMessage(error), 'error'),
    );
    const stopSettings = onSnapshot(doc(db, `users/${user.uid}/settings/main`), (snapshot) => {
      if (snapshot.exists()) setSettings(snapshot.data() as SettingsData);
    });
    return () => {
      stopSessions();
      stopEnvironments();
      stopSettings();
    };
  }, [ownerReady, user]);

  useEffect(() => {
    if (!ownerReady) return;
    let cancelled = false;
    const checkWorker = async () => {
      try {
        const response = await fetch('https://terminal.devpilot.yansoisson.com/health', {
          cache: 'no-store',
        });
        if (!cancelled) setWorkerOnline(response.ok);
      } catch {
        if (!cancelled) setWorkerOnline(false);
      }
    };
    void checkWorker();
    const timer = window.setInterval(checkWorker, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [ownerReady]);

  const performSessionAction = async (
    session: Session,
    action: 'provision' | 'start' | 'stop' | 'restart' | 'archive' | 'delete',
  ) => {
    try {
      await queueSessionAction(user!.uid, session.id, action);
      toast(`${action[0]!.toUpperCase()}${action.slice(1)} requested.`, 'success');
      if (action === 'delete' && selectedSessionId === session.id) setSelectedSessionId(null);
    } catch (error) {
      toast(errorMessage(error), 'error');
    }
  };

  if (!authReady) return <LoadingScreen label="Preparing your cloud workspace" />;
  if (!user) return <SignInScreen error={authError} setError={setAuthError} />;
  if (!ownerReady) {
    return authError ? (
      <AccessDenied user={user} error={authError} />
    ) : (
      <LoadingScreen label="Verifying private workspace access" />
    );
  }

  return (
    <div className="app-frame">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <TerminalSquare size={19} strokeWidth={2.2} />
          </div>
          <div>
            <strong>DevPilot</strong>
            <span>Personal cloud</span>
          </div>
        </div>

        <button
          className="new-session-button"
          type="button"
          onClick={() => setShowNewSession(true)}
        >
          <Plus size={17} /> New session
          <kbd>⌘ K</kbd>
        </button>

        <nav className="primary-nav" aria-label="Primary navigation">
          <NavButton
            active={view === 'sessions'}
            onClick={() => setView('sessions')}
            icon={<Code2 />}
          >
            Sessions
          </NavButton>
          <NavButton
            active={view === 'environments'}
            onClick={() => setView('environments')}
            icon={<Box />}
          >
            Environments
          </NavButton>
          <NavButton
            active={view === 'settings'}
            onClick={() => setView('settings')}
            icon={<Settings />}
          >
            Settings
          </NavButton>
        </nav>

        <div className="sidebar-section-heading">
          <span>Recent sessions</span>
          <span>{sessions.filter((session) => !session.archived).length}</span>
        </div>
        <div className="session-list">
          {sessions
            .filter((session) => !session.archived)
            .slice(0, 12)
            .map((session) => (
              <button
                className={`session-row ${selectedSessionId === session.id ? 'active' : ''}`}
                type="button"
                key={session.id}
                onClick={() => {
                  setView('sessions');
                  setSelectedSessionId(session.id);
                }}
              >
                <span
                  className="agent-dot"
                  style={{ background: agentCatalog[session.agent].accent }}
                />
                <span>
                  <strong>{session.title}</strong>
                  <small>{agentCatalog[session.agent].label}</small>
                </span>
                <StatusDot status={session.status} />
              </button>
            ))}
          {sessions.length === 0 ? <p className="sidebar-empty">No sessions yet.</p> : null}
        </div>

        <div className="sidebar-footer">
          <div className={`worker-health ${workerOnline ? 'online' : 'offline'}`}>
            <span className="health-pulse" />
            <div>
              <strong>VPS worker</strong>
              <small>{workerOnline ? 'Online' : 'Waiting for connection'}</small>
            </div>
          </div>
          <button className="profile-button" type="button" title="Account">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" />
            ) : (
              <span>{user.displayName?.[0] ?? 'Y'}</span>
            )}
            <div>
              <strong>{user.displayName ?? 'Owner'}</strong>
              <small>{user.email}</small>
            </div>
            <ChevronDown size={15} />
          </button>
        </div>
      </aside>

      <main className="main-canvas">
        {view === 'sessions' ? (
          selectedSession ? (
            <SessionWorkspace
              session={selectedSession}
              environment={environments.find((item) => item.id === selectedSession.environmentId)}
              user={user}
              onAction={(action) => performSessionAction(selectedSession, action)}
              toast={toast}
            />
          ) : (
            <EmptyWorkspace onCreate={() => setShowNewSession(true)} />
          )
        ) : null}
        {view === 'environments' ? (
          <EnvironmentsView
            environments={environments}
            onCreate={() => setShowNewEnvironment(true)}
            onDelete={async (environment) => {
              if (!window.confirm(`Delete “${environment.name}”?`)) return;
              try {
                await deleteDoc(doc(db, `users/${user.uid}/environments/${environment.id}`));
                toast('Environment deleted.', 'success');
              } catch (error) {
                toast(errorMessage(error), 'error');
              }
            }}
          />
        ) : null}
        {view === 'settings' ? (
          <SettingsView
            user={user}
            settings={settings}
            onSave={async (next, ollamaApiKey) => {
              try {
                const batch = writeBatch(db);
                const settingsUpdate: SettingsData & {
                  updatedAt: ReturnType<typeof serverTimestamp>;
                } = {
                  ...next,
                  updatedAt: serverTimestamp(),
                };
                if (ollamaApiKey) {
                  settingsUpdate.ollamaApiKeyConfigured = false;
                  settingsUpdate.ollamaApiKeyError = null;
                  batch.set(doc(db, `users/${user.uid}/secrets/opencode`), {
                    key: ollamaApiKey,
                    status: 'queued',
                    version: crypto.randomUUID(),
                    updatedAt: serverTimestamp(),
                  });
                }
                batch.set(doc(db, `users/${user.uid}/settings/main`), settingsUpdate, {
                  merge: true,
                });
                await batch.commit();
                toast(
                  ollamaApiKey
                    ? 'Settings saved. Installing the Ollama Cloud key…'
                    : 'Settings saved.',
                  'success',
                );
              } catch (error) {
                toast(errorMessage(error), 'error');
                throw error;
              }
            }}
            onOpenLogin={async () => {
              try {
                const sessionId = await createSessionRecord(user.uid, {
                  title: 'Toolchain & logins',
                  agent: 'shell',
                  model: '',
                  effort: '',
                  environmentId: null,
                });
                setSelectedSessionId(sessionId);
                setView('sessions');
                toast('Login terminal is being prepared.', 'success');
              } catch (error) {
                toast(errorMessage(error), 'error');
              }
            }}
          />
        ) : null}
      </main>

      {showNewSession ? (
        <NewSessionModal
          environments={environments}
          settings={settings}
          onClose={() => setShowNewSession(false)}
          onSubmit={async (input) => {
            const sessionId = await createSessionRecord(user.uid, input);
            setSelectedSessionId(sessionId);
            setView('sessions');
            setShowNewSession(false);
            toast('Session queued on your VPS.', 'success');
          }}
        />
      ) : null}
      {showNewEnvironment ? (
        <NewEnvironmentModal
          onClose={() => setShowNewEnvironment(false)}
          onSubmit={async (input) => {
            await addDoc(collection(db, `users/${user.uid}/environments`), {
              ...input,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            setShowNewEnvironment(false);
            toast('Environment created.', 'success');
          }}
        />
      ) : null}

      <div className="toast-stack" aria-live="polite">
        {toasts.map((item) => (
          <div className={`toast ${item.tone}`} key={item.id}>
            {item.tone === 'success' ? (
              <Check size={16} />
            ) : item.tone === 'error' ? (
              <X size={16} />
            ) : (
              <Cloud size={16} />
            )}
            {item.message}
          </div>
        ))}
      </div>
    </div>
  );
}

function SignInScreen({ error, setError }: { error: string; setError: (value: string) => void }) {
  const [busy, setBusy] = useState(false);
  const signIn = async () => {
    setBusy(true);
    setError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (nextError) {
      setError(errorMessage(nextError));
      setBusy(false);
    }
  };
  return (
    <div className="auth-page">
      <div className="auth-orbit orbit-one" />
      <div className="auth-orbit orbit-two" />
      <section className="auth-card">
        <div className="auth-brand-mark">
          <TerminalSquare size={25} />
        </div>
        <span className="eyebrow">
          <ShieldCheck size={14} /> Private workspace
        </span>
        <h1>
          Your agents.
          <br />
          Always running.
        </h1>
        <p>Persistent coding terminals on your private VPS, available wherever you are.</p>
        <button className="google-button" type="button" onClick={signIn} disabled={busy}>
          {busy ? <LoaderCircle className="spin" size={19} /> : <GoogleGlyph />}
          Continue with Google
        </button>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="auth-features">
          <span>
            <Wifi size={14} /> Survives disconnects
          </span>
          <span>
            <Container size={14} /> Isolated containers
          </span>
          <span>
            <KeyRound size={14} /> Subscription logins
          </span>
        </div>
      </section>
    </div>
  );
}

function AccessDenied({ user, error }: { user: User; error: string }) {
  return (
    <div className="auth-page">
      <section className="auth-card denied-card">
        <ShieldCheck size={32} />
        <h1>Private workspace</h1>
        <p>{error}</p>
        <small>Signed in as {user.email}</small>
        <button className="google-button" type="button" onClick={() => signOut(auth)}>
          <LogOut size={18} /> Use another account
        </button>
      </section>
    </div>
  );
}

function LoadingScreen({ label }: { label: string }) {
  return (
    <div className="loading-screen">
      <div className="loading-mark">
        <TerminalSquare size={24} />
      </div>
      <LoaderCircle className="spin" size={19} />
      <span>{label}</span>
    </div>
  );
}

function NavButton({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button className={active ? 'active' : ''} type="button" onClick={onClick}>
      {icon}
      {children}
    </button>
  );
}

function StatusDot({ status }: { status: Session['status'] }) {
  return <span className={`status-dot ${status}`} title={status} />;
}

function EmptyWorkspace({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="empty-workspace">
      <div className="empty-visual">
        <span className="code-line l1" />
        <span className="code-line l2" />
        <span className="code-line l3" />
        <TerminalSquare size={36} />
      </div>
      <span className="eyebrow">
        <Sparkles size={14} /> Ready when you are
      </span>
      <h2>Start a cloud coding session</h2>
      <p>
        Choose an agent and environment. Your terminal will keep running even when this device goes
        offline.
      </p>
      <button className="primary-button" type="button" onClick={onCreate}>
        <Plus size={17} /> New session
      </button>
    </div>
  );
}

function SessionWorkspace({
  session,
  environment,
  user,
  onAction,
  toast,
}: {
  session: Session;
  environment?: Environment;
  user: User;
  onAction: (action: 'provision' | 'start' | 'stop' | 'restart' | 'archive' | 'delete') => void;
  toast: (message: string, tone?: Toast['tone']) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const sshCommand = `ssh -i ~/.ssh/devpilot_ed25519 -o IdentitiesOnly=yes -t devpilot@terminal.devpilot.yansoisson.com "devpilot attach ${session.id}"`;
  const agent = agentCatalog[session.agent];

  const uploadFiles = async (files: FileList | File[]) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 100 * 1024 * 1024) throw new Error(`${file.name} is larger than 100 MB.`);
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '-');
        const storagePath = `users/${user.uid}/sessions/${session.id}/attachments/${crypto.randomUUID()}-${safeName}`;
        const task = uploadBytesResumable(ref(storage, storagePath), file, {
          contentType: file.type || 'application/octet-stream',
          customMetadata: { originalName: file.name, sessionId: session.id },
        });
        await new Promise<void>((resolve, reject) =>
          task.on('state_changed', undefined, reject, resolve),
        );
        await addDoc(collection(db, `users/${user.uid}/sessions/${session.id}/attachments`), {
          storagePath,
          name: file.name,
          size: file.size,
          status: 'uploaded',
          createdAt: serverTimestamp(),
        });
      }
      toast(`${files.length} attachment${files.length === 1 ? '' : 's'} uploaded.`, 'success');
    } catch (error) {
      toast(errorMessage(error), 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section
      className="workspace"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        void uploadFiles(event.dataTransfer.files);
      }}
    >
      <header className="workspace-header">
        <div className="workspace-title">
          <div
            className="agent-icon"
            style={{ '--agent-color': agent.accent } as React.CSSProperties}
          >
            <Code2 size={18} />
          </div>
          <div>
            <div className="title-line">
              <h1>{session.title}</h1>
              <StatusPill status={session.status} />
            </div>
            <div className="session-meta">
              <span>{agent.label}</span>
              {session.model ? (
                <>
                  <i /> <span>{session.model}</span>
                </>
              ) : null}
              {environment ? (
                <>
                  <i />{' '}
                  <span>
                    <FolderGit2 size={13} /> {environment.name}
                  </span>
                </>
              ) : (
                <>
                  <i />
                  <span>Scratch workspace</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="workspace-actions">
          {session.status === 'error' && !session.containerName ? (
            <button type="button" onClick={() => onAction('provision')}>
              <RotateCcw size={15} /> Retry
            </button>
          ) : session.status === 'stopped' || session.status === 'error' ? (
            <button type="button" onClick={() => onAction('start')}>
              <Play size={15} /> Start
            </button>
          ) : (
            <button type="button" onClick={() => onAction('stop')}>
              <CircleStop size={15} /> Stop
            </button>
          )}
          <button type="button" onClick={() => onAction('restart')}>
            <RefreshCw size={15} /> Restart
          </button>
          <SessionActionsMenu onAction={onAction} />
        </div>
      </header>

      <div className="workspace-toolbar">
        <div className="toolbar-group">
          <span>
            <Server size={14} /> {session.containerName ?? 'Container pending'}
          </span>
          <span>
            <Laptop size={14} /> tmux: agent
          </span>
        </div>
        <div className="toolbar-group right">
          <input
            ref={inputRef}
            hidden
            type="file"
            multiple
            onChange={(event) => event.target.files && void uploadFiles(event.target.files)}
          />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? <LoaderCircle className="spin" size={14} /> : <Paperclip size={14} />}{' '}
            Attach
          </button>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(sshCommand);
              toast('SSH command copied.', 'success');
            }}
          >
            <Copy size={14} /> SSH
          </button>
        </div>
      </div>

      {session.status === 'running' ? (
        <Terminal sessionId={session.id} user={user} active />
      ) : (
        <div className="terminal-placeholder">
          {session.status === 'queued' || session.status === 'provisioning' ? (
            <>
              <LoaderCircle className="spin" size={23} />
              <strong>Preparing your session</strong>
              <span>
                The VPS worker will attach the terminal as soon as the container is ready.
              </span>
            </>
          ) : session.status === 'error' ? (
            <>
              <X size={23} />
              <strong>Container could not start</strong>
              <span>{session.lastError ?? 'Check the VPS worker and try again.'}</span>
              <button
                className="primary-button"
                type="button"
                onClick={() => onAction(session.containerName ? 'restart' : 'provision')}
              >
                <RotateCcw size={15} /> Retry
              </button>
            </>
          ) : (
            <>
              <CircleStop size={23} />
              <strong>Session stopped</strong>
              <span>Your workspace and conversation state are preserved.</span>
              <button className="primary-button" type="button" onClick={() => onAction('start')}>
                <Play size={15} /> Start session
              </button>
            </>
          )}
        </div>
      )}
      <div className="drop-hint">
        <UploadCloud size={17} /> Drop files anywhere to attach
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: Session['status'] }) {
  return (
    <span className={`status-pill ${status}`}>
      <i />
      {status}
    </span>
  );
}

function EnvironmentsView({
  environments,
  onCreate,
  onDelete,
}: {
  environments: Environment[];
  onCreate: () => void;
  onDelete: (environment: Environment) => void;
}) {
  return (
    <section className="content-page">
      <PageHeader
        eyebrow="Runtime configuration"
        title="Environments"
        description="Repositories, setup commands, and credentials prepared for every new session."
      >
        <button className="primary-button" type="button" onClick={onCreate}>
          <Plus size={16} /> New environment
        </button>
      </PageHeader>
      <div className="environment-grid">
        <article className="environment-card scratch-card">
          <div className="environment-icon">
            <Sparkles size={18} />
          </div>
          <div>
            <span className="card-kicker">Built in</span>
            <h3>No project</h3>
            <p>Start with an empty persistent workspace.</p>
          </div>
          <span className="environment-badge">Always available</span>
        </article>
        {environments.map((environment) => (
          <article className="environment-card" key={environment.id}>
            <div className="environment-card-top">
              <div className="environment-icon">
                <FolderGit2 size={18} />
              </div>
              <button
                className="icon-button danger-hover"
                type="button"
                onClick={() => onDelete(environment)}
                title="Delete environment"
              >
                <Trash2 size={15} />
              </button>
            </div>
            <span className="card-kicker">Git environment</span>
            <h3>{environment.name}</h3>
            <p>{environment.repositoryUrl || 'No remote repository'}</p>
            <div className="environment-details">
              <span>
                <Code2 size={13} /> {environment.defaultBranch}
              </span>
              <span>
                <KeyRound size={13} /> {environment.credentialProfileIds.length} profiles
              </span>
            </div>
          </article>
        ))}
        <button className="environment-card add-card" type="button" onClick={onCreate}>
          <Plus size={24} />
          <strong>Add environment</strong>
          <span>Connect another repository</span>
        </button>
      </div>
    </section>
  );
}

function SettingsView({
  user,
  settings,
  onSave,
  onOpenLogin,
}: {
  user: User;
  settings: SettingsData;
  onSave: (settings: SettingsData, ollamaApiKey: string) => Promise<void>;
  onOpenLogin: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<SettingsData>(settings);
  const [ollamaApiKey, setOllamaApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(settings), [settings]);
  return (
    <section className="content-page settings-page">
      <PageHeader
        eyebrow="Private workspace"
        title="Settings"
        description="Defaults and subscription logins used across your cloud sessions."
      />
      <div className="settings-layout">
        <div className="settings-column">
          <SettingsSection
            icon={<Sparkles />}
            title="Agent defaults"
            description="Used when you create a new session."
          >
            <label className="field-label">
              Default agent
              <select
                value={draft.defaultAgent ?? 'codex'}
                onChange={(event) =>
                  setDraft({ ...draft, defaultAgent: event.target.value as AgentId })
                }
              >
                {Object.entries(agentCatalog)
                  .filter(([id]) => id !== 'shell')
                  .map(([id, item]) => (
                    <option key={id} value={id}>
                      {item.label}
                    </option>
                  ))}
              </select>
            </label>
            <div className="agent-default-list">
              {(Object.keys(agentCatalog) as AgentId[])
                .filter((agent) => agent !== 'shell')
                .map((agent) => (
                  <div className="agent-default-row" key={agent}>
                    <span
                      className="agent-dot large"
                      style={{ background: agentCatalog[agent].accent }}
                    />
                    <div>
                      <strong>{agentCatalog[agent].label}</strong>
                      <small>Default model and intelligence</small>
                    </div>
                    <input
                      aria-label={`${agentCatalog[agent].label} model`}
                      placeholder="Native default"
                      value={draft.defaultModels?.[agent] ?? ''}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          defaultModels: { ...draft.defaultModels, [agent]: event.target.value },
                        })
                      }
                    />
                    {agentCatalog[agent].efforts.length ? (
                      <select
                        aria-label={`${agentCatalog[agent].label} effort`}
                        value={draft.defaultEfforts?.[agent] ?? ''}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            defaultEfforts: {
                              ...draft.defaultEfforts,
                              [agent]: event.target.value,
                            },
                          })
                        }
                      >
                        <option value="">Default</option>
                        {agentCatalog[agent].efforts.map((effort) => (
                          <option key={effort} value={effort}>
                            {effort}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="native-model-note">Model variant</span>
                    )}
                  </div>
                ))}
            </div>
          </SettingsSection>
          <button
            className="primary-button save-settings"
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(draft, ollamaApiKey.trim());
                setOllamaApiKey('');
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />} Save
            changes
          </button>
        </div>
        <div className="settings-column">
          <SettingsSection
            icon={<KeyRound />}
            title="Toolchain & logins"
            description="Authenticate once using the original CLI flows."
          >
            <label className="field-label api-key-field">
              Ollama Cloud API key
              <input
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={ollamaApiKey}
                onChange={(event) => setOllamaApiKey(event.target.value)}
                placeholder={
                  settings.ollamaApiKeyConfigured
                    ? 'Configured — enter a new key to replace it'
                    : 'Paste your Ollama Cloud API key'
                }
              />
              <span>
                {settings.ollamaApiKeyError ? (
                  <span className="credential-state error">{settings.ollamaApiKeyError}</span>
                ) : settings.ollamaApiKeyConfigured ? (
                  <span className="credential-state configured">
                    <Check size={13} /> Stored in OpenCode's native credential file
                  </span>
                ) : (
                  'Saved through Firebase and installed into the private VPS credential profile.'
                )}
              </span>
            </label>
            <div className="login-summary">
              <div>
                <ShieldCheck size={21} />
                <span>
                  <strong>Private VPS profile</strong>
                  <small>Credentials stay in a root-restricted VPS directory.</small>
                </span>
              </div>
              <span className="setup-badge">Setup required</span>
            </div>
            <button
              className="secondary-button full-width"
              type="button"
              onClick={() => void onOpenLogin()}
            >
              <TerminalSquare size={16} /> Open login terminal
            </button>
            <p className="settings-note">
              Use this terminal for Codex, Claude, Grok, Antigravity, OpenCode, GitHub, and other
              developer CLI logins. Restart an existing OpenCode session after replacing its key.
            </p>
          </SettingsSection>
          <SettingsSection
            icon={<ShieldCheck />}
            title="Owner account"
            description="The only Google account authorized for DevPilot."
          >
            <div className="owner-profile">
              {user.photoURL ? (
                <img src={user.photoURL} alt="" />
              ) : (
                <span>{user.displayName?.[0]}</span>
              )}
              <div>
                <strong>{user.displayName}</strong>
                <small>{user.email}</small>
                <code>{user.uid}</code>
              </div>
            </div>
            <button className="text-button" type="button" onClick={() => signOut(auth)}>
              <LogOut size={15} /> Sign out
            </button>
          </SettingsSection>
        </div>
      </div>
    </section>
  );
}

function SettingsSection({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="settings-section">
      <header>
        <span>{icon}</span>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </header>
      <div className="settings-section-body">{children}</div>
    </section>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {children}
    </header>
  );
}

function NewSessionModal({
  environments,
  settings,
  onClose,
  onSubmit,
}: {
  environments: Environment[];
  settings: SettingsData;
  onClose: () => void;
  onSubmit: (input: {
    title: string;
    agent: AgentId;
    model: string;
    effort: string;
    environmentId: string | null;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [agent, setAgent] = useState<AgentId>(settings.defaultAgent ?? 'codex');
  const [environmentId, setEnvironmentId] = useState('');
  const [model, setModel] = useState(
    settings.defaultModels?.[settings.defaultAgent ?? 'codex'] ?? '',
  );
  const [effort, setEffort] = useState(
    settings.defaultEfforts?.[settings.defaultAgent ?? 'codex'] ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const agents = (Object.keys(agentCatalog) as AgentId[]).filter((item) => item !== 'shell');
  return (
    <Modal
      title="New cloud session"
      subtitle="A fresh container with a terminal that stays online."
      onClose={onClose}
    >
      <form
        className="modal-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          try {
            await onSubmit({ title, agent, model, effort, environmentId: environmentId || null });
          } catch (nextError) {
            setError(errorMessage(nextError));
            setBusy(false);
          }
        }}
      >
        <label className="field-label">
          Session name
          <input
            autoFocus
            required
            maxLength={120}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Build the new dashboard"
          />
        </label>
        <fieldset className="agent-picker">
          <legend>Agent</legend>
          {agents.map((id) => (
            <button
              className={agent === id ? 'selected' : ''}
              type="button"
              key={id}
              onClick={() => {
                setAgent(id);
                setModel(settings.defaultModels?.[id] ?? '');
                setEffort(settings.defaultEfforts?.[id] ?? '');
              }}
            >
              <span className="agent-dot large" style={{ background: agentCatalog[id].accent }} />
              <strong>{agentCatalog[id].label}</strong>
              {agent === id ? <Check size={15} /> : null}
            </button>
          ))}
        </fieldset>
        <div className="two-fields">
          <label className="field-label">
            Environment
            <select
              value={environmentId}
              onChange={(event) => setEnvironmentId(event.target.value)}
            >
              <option value="">No project</option>
              {environments.map((environment) => (
                <option key={environment.id} value={environment.id}>
                  {environment.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            Model
            <input
              value={model}
              onChange={(event) => setModel(event.target.value)}
              placeholder="Native default"
            />
          </label>
        </div>
        {agentCatalog[agent].efforts.length ? (
          <label className="field-label">
            Intelligence
            <select value={effort} onChange={(event) => setEffort(event.target.value)}>
              <option value="">Native default</option>
              {agentCatalog[agent].efforts.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy || !title.trim()}>
            {busy ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />} Create session
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NewEnvironmentModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    repositoryUrl: string;
    defaultBranch: string;
    setupCommand: string;
    dotenv: string;
    credentialProfileIds: string[];
  }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [setupCommand, setSetupCommand] = useState('');
  const [dotenv, setDotenv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <Modal
      title="New environment"
      subtitle="Define how DevPilot prepares a project workspace."
      onClose={onClose}
    >
      <form
        className="modal-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          try {
            await onSubmit({
              name,
              repositoryUrl,
              defaultBranch,
              setupCommand,
              dotenv,
              credentialProfileIds: [],
            });
          } catch (nextError) {
            setError(errorMessage(nextError));
            setBusy(false);
          }
        }}
      >
        <label className="field-label">
          Name
          <input
            autoFocus
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="DevPilot production"
          />
        </label>
        <label className="field-label">
          GitHub repository <span>optional</span>
          <input
            type="url"
            value={repositoryUrl}
            onChange={(event) => setRepositoryUrl(event.target.value)}
            placeholder="https://github.com/owner/repository.git"
          />
        </label>
        <label className="field-label">
          Default branch
          <input
            required
            value={defaultBranch}
            onChange={(event) => setDefaultBranch(event.target.value)}
          />
        </label>
        <label className="field-label">
          Environment file <span>optional · saved as .env</span>
          <textarea
            rows={6}
            value={dotenv}
            onChange={(event) => setDotenv(event.target.value)}
            placeholder={'NODE_ENV=development\nSERVICE_TOKEN=…'}
            spellCheck={false}
          />
        </label>
        <label className="field-label">
          Setup command <span>optional</span>
          <textarea
            rows={4}
            value={setupCommand}
            onChange={(event) => setSetupCommand(event.target.value)}
            placeholder="npm install"
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />} Create
            environment
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.24 1.06-3.71 1.06-2.86 0-5.29-1.94-6.16-4.54H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.36c1.62 0 3.06.56 4.2 1.64l3.15-3.15A10.55 10.55 0 0 0 12 1a11 11 0 0 0-9.82 6.07l3.66 2.84c.87-2.6 3.3-4.55 6.16-4.55Z"
      />
    </svg>
  );
}
